/**
 * at-course-catalog.js — Training Course Catalog web component
 * Reference: Asana 1219077459165152 (no Figma node — reference mockup only)
 *
 * Two jobs:
 *   1. Expand/collapse a card's detail panel (accordion, one per card).
 *   2. Filter the cards by free-text search and by category.
 *
 * Filtering is client-side over the already-rendered blocks — the catalog is
 * a short, editor-managed list, so there is nothing to fetch.
 */

if (!customElements.get('course-catalog')) {
  const DEBOUNCE_MS = 150;

  class CourseCatalog extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-catalog-item]')];
      this.searchInput = this.querySelector('[data-catalog-search]');
      this.categorySelect = this.querySelector('[data-catalog-category]');
      this.statusEl = this.querySelector('[data-catalog-status]');
      this.emptyEl = this.querySelector('[data-catalog-empty]');

      this.addEventListener('click', this.handleClick);
      this.searchInput?.addEventListener('input', this.handleFilterInput);
      this.categorySelect?.addEventListener('change', this.applyFilters);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.searchInput?.removeEventListener('input', this.handleFilterInput);
      this.categorySelect?.removeEventListener('change', this.applyFilters);
      clearTimeout(this.debounceId);
    }

    /* Accordion ----------------------------------------------------------- */

    handleClick = (event) => {
      const toggle = event.target.closest('[data-catalog-toggle]');
      if (!toggle || !this.contains(toggle)) return;

      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;

      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(willOpen));
      panel.hidden = !willOpen;
      toggle.closest('[data-catalog-item]')?.classList.toggle('at-course-catalog__item--open', willOpen);
    };

    /* Filtering ----------------------------------------------------------- */

    handleFilterInput = () => {
      clearTimeout(this.debounceId);
      this.debounceId = setTimeout(this.applyFilters, DEBOUNCE_MS);
    };

    applyFilters = () => {
      const term = (this.searchInput?.value ?? '').trim().toLowerCase();
      const category = this.categorySelect?.value ?? '';

      let visible = 0;

      this.items.forEach((item) => {
        const matchesCategory = category === '' || item.dataset.category === category;
        const matchesTerm = term === '' || (item.dataset.search ?? '').includes(term);
        const show = matchesCategory && matchesTerm;

        item.hidden = !show;
        if (show) {
          visible += 1;
        } else {
          // A hidden card must not keep an open panel behind it, or the next
          // search leaves the card expanded for no visible reason.
          this.collapse(item);
        }
      });

      if (this.emptyEl) this.emptyEl.hidden = visible !== 0;
      if (this.statusEl) {
        this.statusEl.textContent =
          visible === 1 ? '1 course category shown' : `${visible} course categories shown`;
      }
    };

    collapse(item) {
      const toggle = item.querySelector('[data-catalog-toggle]');
      const panel = item.querySelector('[data-catalog-panel]');
      if (!toggle || !panel) return;

      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      item.classList.remove('at-course-catalog__item--open');
    }
  }

  customElements.define('course-catalog', CourseCatalog);
}
